import { useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import type { Sesion } from '../sesion/sesion';
import { useSesion } from '../sesion/sesion';
import { ETIQUETA_ROL, puede } from '../sesion/permisos';
import type { Permiso } from '../sesion/permisos';
import type { RolOperacion } from '../dominio/contratos';
import { entorno, hayBackend } from '../config/entorno';
import { useCargaViva } from '../dominio/carga';
import { obtenerPanel } from '../dominio/panelApi';

/* --------------------------------------------------------------------------
 * Reloj — el elemento firma de la consola
 *
 * R-07: toda transicion del sistema se sella con HORA DE SERVIDOR, nunca con la
 * del dispositivo. Esta es la pantalla donde una persona lee esas horas para
 * resolver una disputa, asi que la hora de referencia esta siempre a la vista.
 *
 * ⚠️ Y por eso mismo dice de donde sale: sin backend no hay hora de servidor, y
 * mostrar la del navegador como si lo fuera es exactamente el error que R-07
 * existe para evitar. Mientras tanto se rotula "hora local" y se pinta en ambar.
 * -------------------------------------------------------------------------- */
function Reloj() {
  const [ahora, setAhora] = useState(() => new Date());

  useEffect(() => {
    const t = window.setInterval(() => setAhora(new Date()), 1000);
    return () => window.clearInterval(t);
  }, []);

  const hora = new Intl.DateTimeFormat('es-PE', {
    timeZone: 'America/Lima',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(ahora);

  return (
    <div className={`reloj ${hayBackend ? '' : 'reloj--sin-servidor'}`}>
      <span className="rotulo">{hayBackend ? 'Hora de servidor' : 'Hora local'}</span>
      <time className="reloj__hora" dateTime={ahora.toISOString()}>
        {hora}
      </time>
    </div>
  );
}

/* --------------------------------------------------------------------------
 * Navegacion
 *
 * Agrupada por lo que hace el turno, no por como esta armado el sistema. El
 * ultimo grupo esta separado porque su contenido cambia el ingreso o la
 * relacion contractual con el conductor: es el corte de rol de CA-10.1, y se ve.
 * -------------------------------------------------------------------------- */
interface Entrada {
  a: string;
  texto: string;
  permiso: Permiso;
  /**
   * `number` = cuenta real del backend. `undefined` = esta entrada no lleva contador.
   * **Nunca 0 por "no se pudo leer"**: cuando no hay dato, `cuentasDelMenu` es `null` y la entrada
   * queda SIN contador. Un `0` inventado y un `0` real se ven idénticos, y el badge existe
   * justamente para decidir a dónde mirar primero.
   */
  cuenta?: number | undefined;
}

/** Los dos contadores del menú, o `null` cuando todavía no hay dato del backend. */
interface CuentasDelMenu {
  enColaVerificacion: number;
  incidentesAbiertos: number;
}

/**
 * Cada cuánto se refrescan los contadores del menú.
 *
 * 🔑 **No reusa `entorno.mapa.refrescoMs` (5 s) a propósito**: ése dimensiona el mapa de operación,
 * donde una posición vieja manda a un despachador al lugar equivocado. Un badge de menú es una
 * señal de "mirá para acá", no un dato operativo; a 5 s costaría 12× más peticiones por el mismo
 * valor de decisión. `Marco` vive en TODAS las pantallas, así que su costo se paga siempre.
 */
const REFRESCO_CUENTAS_MS = 60_000;

/**
 * Reusa `GET /consola/panel` en vez de un endpoint propio: ya devuelve los dos números exactos que
 * el menú necesita, y agregar superficie de backend para dos enteros no se justifica.
 *
 * ⚠️ **Costo declarado, no escondido:** el panel calcula 8 `count()` + la tasa de victoria, y de eso
 * el menú usa 2 campos. Estando en `Panel.tsx` se hacen 2 llamadas al mismo endpoint. Si alguna vez
 * pesa, el arreglo es un endpoint liviano de contadores — no bajar el refresco hasta que el badge
 * deje de significar algo.
 */
async function cargarCuentasDelMenu(): Promise<CuentasDelMenu> {
  if (!hayBackend) {
    throw new Error('Sin backend configurado: el menú no muestra contadores.');
  }
  const panel = await obtenerPanel();
  return {
    enColaVerificacion: panel.conductoresEnColaVerificacion,
    incidentesAbiertos: panel.incidentesAbiertos,
  };
}

interface Grupo {
  titulo: string;
  entradas: Entrada[];
}

function gruposPara(rol: RolOperacion, cuentas: CuentasDelMenu | null): Grupo[] {
  const enCola = cuentas?.enColaVerificacion;
  const abiertos = cuentas?.incidentesAbiertos;

  const todos: Grupo[] = [
    {
      titulo: 'Turno',
      entradas: [
        { a: '/', texto: 'Panel', permiso: 'ver.panel' },
        { a: '/mapa', texto: 'Mapa de operación', permiso: 'ver.mapa' },
        { a: '/incidentes', texto: 'Incidentes', permiso: 'ver.incidentes', cuenta: abiertos },
      ],
    },
    {
      titulo: 'Conductores',
      entradas: [
        {
          a: '/verificacion',
          texto: 'Cola de verificación',
          permiso: 'revisar.documentos',
          cuenta: enCola,
        },
        { a: '/conductores', texto: 'Buscar conductores', permiso: 'ver.conductores' },
        { a: '/suscripciones', texto: 'Suscripciones', permiso: 'ver.suscripciones' },
      ],
    },
    {
      titulo: 'Viajes',
      entradas: [{ a: '/viajes', texto: 'Buscar viajes', permiso: 'ver.viajes' }],
    },
    {
      titulo: 'Solo administrador',
      entradas: [
        { a: '/dispatch', texto: 'Parámetros de dispatch', permiso: 'editar.dispatch' },
        { a: '/tarifario', texto: 'Tarifario y zonas', permiso: 'editar.tarifario' },
        // Va DESPUÉS del tarifario a propósito: el tarifario dice cuánto sugerir, la política
        // dice si eso obliga a alguien. Leerlas en ese orden es el orden en que se deciden.
        { a: '/precio', texto: 'Política de precio', permiso: 'editar.precio' },
        // Al lado de la política de precio a propósito: las dos son config que gobierna dinero,
        // sólo que una decide cuánto se le cobra al PASAJERO y ésta cuánto se le cobra al
        // CONDUCTOR por operar.
        {
          a: '/plan-suscripcion',
          texto: 'Plan de suscripción',
          permiso: 'editar.plan-suscripcion',
        },
        { a: '/auditoria', texto: 'Auditoría', permiso: 'ver.auditoria' },
      ],
    },
  ];

  return todos
    .map((grupo) => ({ ...grupo, entradas: grupo.entradas.filter((e) => puede(rol, e.permiso)) }))
    .filter((grupo) => grupo.entradas.length > 0);
}

function Navegacion({ rol }: { rol: RolOperacion }) {
  // `periodoMs = 0` sin backend: carga unica que falla y deja los contadores en `null`. El hook
  // igual se llama SIEMPRE -- las reglas de hooks no permiten saltearlo segun `hayBackend`.
  const carga = useCargaViva(cargarCuentasDelMenu, hayBackend ? REFRESCO_CUENTAS_MS : 0);

  // 🔴 Solo hay contadores con datos REALES en mano. `cargando` y `error` dan `null`, no cero.
  const cuentas = carga.estado === 'listo' ? carga.datos : null;

  // El refresco fallo pero los datos anteriores siguen en pantalla: se marcan como VIEJOS en vez
  // de borrarlos. Un numero que dejo de actualizarse se ve identico a uno vivo, y ese es
  // exactamente el modo de falla que `useCargaViva` existe para hacer visible.
  const viejo = carga.estado === 'listo' && carga.falloRefresco !== null;
  const desde = carga.estado === 'listo' ? carga.actualizadoAt : null;

  return (
    <nav className="nav" aria-label="Secciones de la consola">
      {gruposPara(rol, cuentas).map((grupo) => (
        <div className="nav__grupo" key={grupo.titulo}>
          <p className="nav__titulo rotulo">{grupo.titulo}</p>
          {grupo.entradas.map((entrada) => (
            <NavLink className="nav__enlace" to={entrada.a} end={entrada.a === '/'} key={entrada.a}>
              <span>{entrada.texto}</span>
              {typeof entrada.cuenta === 'number' && entrada.cuenta > 0 ? (
                <span
                  className={`nav__cuenta ${viejo ? 'nav__cuenta--viejo' : ''}`}
                  title={
                    viejo && desde
                      ? `Sin actualizar desde las ${desde.toLocaleTimeString('es-PE')}: el refresco esta fallando.`
                      : undefined
                  }
                >
                  {entrada.cuenta}
                  {viejo ? <span aria-hidden="true"> ?</span> : null}
                </span>
              ) : null}
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
  );
}

/* --------------------------------------------------------------------------
 * Marco
 * -------------------------------------------------------------------------- */
export function Marco({ sesion }: { sesion: Sesion }) {
  const { salir, cambiarRolDeEjemplo } = useSesion();
  const conDatosDeEjemplo = sesion.origen === 'ejemplo';

  return (
    <div className="armazon">
      <a className="salto" href="#principal">
        Saltar al contenido
      </a>

      <div className="marca">
        <span className="marca__luz" aria-hidden="true" />
        <span className="marca__texto">Consola de operación</span>
      </div>

      <header className="cabecera">
        <div className="cabecera__campo">
          <span className="rotulo">Ambiente</span>
          <span className="cabecera__valor">
            {entorno.ambiente === 'sin definir' ? (
              <span style={{ color: 'var(--ambar)' }}>sin definir</span>
            ) : (
              entorno.ambiente
            )}
          </span>
        </div>

        <span className="cabecera__espacio" />

        {conDatosDeEjemplo ? (
          <label className="cabecera__campo">
            <span className="rotulo">Ver como</span>
            <select
              className="selector"
              value={sesion.rol}
              onChange={(evento) => cambiarRolDeEjemplo(evento.target.value as RolOperacion)}
              style={{ padding: '2px 6px' }}
            >
              <option value="operador">Operador</option>
              <option value="administrador">Administrador</option>
            </select>
          </label>
        ) : (
          <div className="cabecera__campo">
            <span className="rotulo">{ETIQUETA_ROL[sesion.rol]}</span>
            <span className="cabecera__valor">{sesion.nombre}</span>
          </div>
        )}

        <Reloj />

        {hayBackend ? (
          <button type="button" className="boton boton--chico" onClick={salir}>
            Cerrar sesión
          </button>
        ) : null}
      </header>

      <Navegacion rol={sesion.rol} />

      {conDatosDeEjemplo ? (
        <div className="aviso-ejemplo" role="status">
          <span className="aviso-ejemplo__marca">◆</span>
          <span>
            Datos de ejemplo. La consola todavía no está conectada al backend, así que nada de lo
            que se ve acá es real y ninguna acción tiene efecto.
          </span>
        </div>
      ) : (
        <div style={{ gridArea: 'aviso' }} />
      )}

      <main className="principal" id="principal" tabIndex={-1}>
        <Outlet />
      </main>
    </div>
  );
}
